import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import ZoomImage from "./ZoomImage";
import { Check, X, Upload } from "lucide-react";

const PURITIES = ["18K", "20K", "22K", "24K"];
const ADVANCE_PCT = 80;
const GST_PCT = 0.03;
const MAKING_BY_PURITY = { "18K": 0.22, "20K": 0.16, "22K": 0.15, "24K": 0.15 };

// Custom-order pricing (mirrors the server): weight × per-gram rate, + purity-based
// making, then 3% GST. The stored rate is GST-inclusive, so strip it first.
function customBreakdown(weight, ratePerGramInclGst, purity) {
  const w = parseFloat(weight) || 0;
  if (w <= 0 || !ratePerGramInclGst) return { net: 0, gst: 0, total: 0, makingPct: 0 };
  const makingPct = MAKING_BY_PURITY[purity] ?? 0.15;
  const gold = (ratePerGramInclGst / (1 + GST_PCT)) * w;
  const net = gold + gold * makingPct;
  const gst = net * GST_PCT;
  return { net: Math.round(net), gst: Math.round(gst), total: Math.round(net + gst), makingPct: Math.round(makingPct * 100) };
}

/**
 * CustomiseDesign — 8 design tiles (4 fastest-selling in the same category +
 * weight window, 4 admin-curated), each hover-enlarges and cycles its images.
 * Click a tile to open a popup that pre-fills weight + purity (editable), then
 * proceed to add the customisation to the cart with that design image attached.
 * Customers can also upload their own reference instead.
 */
export default function CustomiseDesign({ baseProduct = null, onAdded }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [designs, setDesigns] = useState({ bestsellers: [], custom: [], weight_window: null });
  const [rates, setRates] = useState(null);
  const [popup, setPopup] = useState(null);      // design being previewed
  const [selected, setSelected] = useState(null); // { design, images, weight, purity }
  const [weight, setWeight] = useState("");
  const [purity, setPurity] = useState("22K");
  const [note, setNote] = useState("");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const pid = baseProduct?.id ? `?product_id=${baseProduct.id}` : "";
    api.get(`/api/customisation/designs${pid}`).then((r) => setDesigns(r.data || {})).catch(() => {});
    api.get("/api/rates/current").then((r) => setRates(r.data)).catch(() => {});
  }, [baseProduct]);

  const tiles = useMemo(() => [...(designs.bestsellers || []), ...(designs.custom || [])], [designs]);

  const rateFor = (p) => rates ? ({ "24K": rates.gold_24k, "22K": rates.gold_22k, "20K": rates.gold_20k, "18K": rates.gold_18k }[p] || 0) : 0;
  const bd = customBreakdown(weight, rateFor(purity), purity);
  const estimate = bd.total;
  const advance = Math.round(estimate * ADVANCE_PCT / 100);
  const balance = Math.max(estimate - advance, 0);

  const chooseFromPopup = () => {
    setSelected({
      design: popup,
      images: popup.images || [],
      name: popup.name,
      product_id: popup.product_id || baseProduct?.id || null,
    });
    setWeight(popup.weight_grams ? String(popup.weight_grams) : (baseProduct?.weight_grams ? String(baseProduct.weight_grams) : ""));
    setPurity(popup.purity || "22K");
    setPopup(null);
  };

  const useOwnUpload = () => {
    setSelected({ design: null, images: [], name: "Your reference", product_id: baseProduct?.id || null });
    setWeight(baseProduct?.weight_grams ? String(baseProduct.weight_grams) : "");
  };

  const addToCart = async () => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    if (!selected) { toast.error("Pick a design or upload your own reference"); return; }
    if (!selected.product_id) { toast.error("Open this from a product page to customise"); return; }
    if (!(parseFloat(weight) > 0)) { toast.error("Enter the weight in grams"); return; }
    const imgs = [...(selected.images || []), ...uploads].filter(Boolean);
    if (imgs.length === 0) { toast.error("Add at least one reference image"); return; }
    setBusy(true);
    try {
      await api.post("/api/cart/add", {
        product_id: selected.product_id,
        qty: 1,
        is_customised: true,
        custom_weight: parseFloat(weight),
        custom_purity: purity,
        customisation_note: note,
        customisation_images: imgs,
      });
      toast.success("Customisation added to cart");
      setSelected(null); setUploads([]); setNote(""); setWeight("");
      onAdded?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add customisation"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="customise-design">
      <p className="text-[11px] text-ash mb-3">
        Pick one of our designs — or upload your own — then tell us the weight and purity you want.
        We price it on today's gold rate; pay 80% now, the rest on delivery.
      </p>

      {!selected ? (
        <>
          {/* 8-tile design gallery */}
          {tiles.length > 0 ? (
            <div className="grid grid-cols-4 gap-2 mb-3" data-testid="design-tiles">
              {tiles.map((d, i) => (
                <DesignTile key={(d.product_id || d.input_id || i) + "-" + i} design={d} onClick={() => setPopup(d)} />
              ))}
            </div>
          ) : (
            <div className="text-xs text-ash border border-line p-3 mb-3">No designs yet — upload your own reference below.</div>
          )}

          {/* Upload your own */}
          <button onClick={useOwnUpload} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="use-own">
            <Upload size={14}/> Upload my own reference instead
          </button>
        </>
      ) : (
        <div data-testid="customise-selected">
          {/* Selected design summary */}
          <div className="flex items-start gap-3 border border-gold bg-gold/10 p-3 mb-3">
            {selected.images?.[0] && <img src={selected.images[0]} alt="" className="w-20 h-20 object-cover rounded"/>}
            <div className="flex-1">
              <div className="text-sm font-medium">{selected.name}</div>
              <div className="text-[11px] text-ash">This design will be attached to your order and carried through to the karigar.</div>
            </div>
            <button onClick={() => { setSelected(null); setUploads([]); }} className="text-ash hover:text-maroon"><X size={16}/></button>
          </div>

          {/* Weight + purity (editable) */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-[10px] text-ash uppercase tracking-widest">Weight (grams)</span>
              <input type="number" step="0.01" min="0" value={weight} onChange={(e) => setWeight(e.target.value)}
                placeholder="e.g. 12.5" className="w-full border border-line px-3 py-2 bg-pearl mt-1 text-sm" data-testid="custom-weight"/>
            </label>
            <label className="block">
              <span className="text-[10px] text-ash uppercase tracking-widest">Purity</span>
              <select value={purity} onChange={(e) => setPurity(e.target.value)} className="w-full border border-line px-3 py-2 bg-pearl mt-1 text-sm" data-testid="custom-purity">
                {PURITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
          </div>

          {/* Own reference upload (also available with a chosen design) */}
          <div className="mb-3">
            <span className="text-[10px] text-ash uppercase tracking-widest block mb-1">Add your own photos (optional)</span>
            <ImageUploader value={uploads} onChange={setUploads} multiple maxFiles={4} kind="customisation"/>
          </div>
          <textarea rows={2} placeholder="Anything to change? (length, stones, engraving…)"
            className="w-full border border-line px-3 py-2 bg-pearl text-sm mb-3" value={note} onChange={(e) => setNote(e.target.value)} data-testid="custom-note"/>

          {/* Estimate — show only grams, price, GST, total and the 80/20 split */}
          <div className="bg-pearl border border-line p-3 text-sm" data-testid="custom-estimate-box">
            <div className="flex justify-between"><span className="text-ash">Weight</span><span>{weight || 0} g · {purity}</span></div>
            <div className="flex justify-between mt-1"><span className="text-ash">Total price</span><span>{fmtINR(bd.net)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-ash">GST (3%)</span><span>{fmtINR(bd.gst)}</span></div>
            <div className="flex justify-between font-medium mt-1 pt-1 border-t border-line"><span>Total value</span><span>{fmtINR(bd.total)}</span></div>
            <div className="flex justify-between font-medium text-maroon mt-2"><span>Pay now (80% advance)</span><span data-testid="custom-advance">{fmtINR(advance)}</span></div>
            <div className="flex justify-between text-ash text-xs mt-1"><span>Balance on delivery</span><span>{fmtINR(balance)}</span></div>
          </div>

          <button onClick={addToCart} disabled={busy || !(parseFloat(weight) > 0)} className="btn-maroon w-full justify-center mt-3 disabled:opacity-50" data-testid="custom-add">
            {busy ? "Adding…" : "Add customisation to cart"}
          </button>
        </div>
      )}

      {/* Design popup — zoomable images + editable weight/purity */}
      {popup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPopup(null)}>
          <div className="bg-white border border-line w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="design-popup">
            <div className="flex items-center justify-between p-4 border-b border-line">
              <div className="font-display text-xl text-maroon">{popup.name}</div>
              <button onClick={() => setPopup(null)}><X size={18}/></button>
            </div>
            <div className="p-4">
              {/* Images — scroll if multiple, zoom on hover */}
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                {(popup.images || []).map((img, i) => (
                  <ZoomImage key={i} src={img} alt={popup.name} className="w-56 h-56 shrink-0 border border-line rounded" />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                <PopupField label="Category" value={popup.category}/>
                <PopupField label="Metal" value={popup.metal}/>
                <label className="block">
                  <span className="text-[10px] text-ash uppercase tracking-widest">Weight (grams)</span>
                  <input type="number" step="0.01" defaultValue={popup.weight_grams || ""} onChange={(e) => setPopup({ ...popup, weight_grams: e.target.value })}
                    className="w-full border border-line px-3 py-2 bg-pearl mt-1" data-testid="popup-weight"/>
                </label>
                <label className="block">
                  <span className="text-[10px] text-ash uppercase tracking-widest">Purity</span>
                  <select defaultValue={popup.purity || "22K"} onChange={(e) => setPopup({ ...popup, purity: e.target.value })} className="w-full border border-line px-3 py-2 bg-pearl mt-1">
                    {PURITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
              </div>
              {popup.details && <p className="text-xs text-ash mb-3">{popup.details}</p>}
              <button onClick={chooseFromPopup} className="btn-maroon w-full justify-center" data-testid="popup-use">
                <Check size={14}/> Use this design
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** A gallery tile that enlarges on hover and cycles through the product's images. */
function DesignTile({ design, onClick }) {
  const [hover, setHover] = useState(false);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const imgs = design.images || [];

  useEffect(() => {
    if (hover && imgs.length > 1) {
      timer.current = setInterval(() => setIdx((i) => (i + 1) % imgs.length), 900);
    } else {
      setIdx(0);
      if (timer.current) clearInterval(timer.current);
    }
    return () => timer.current && clearInterval(timer.current);
  }, [hover, imgs.length]);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative aspect-square border overflow-hidden rounded transition-transform duration-150 ${hover ? "scale-110 z-10 border-maroon shadow-lg" : "border-line"}`}
      title={design.name}
      data-testid={`design-tile-${design.product_id || design.input_id}`}
    >
      {imgs[idx]
        ? <img src={imgs[idx]} alt={design.name} className="w-full h-full object-cover"/>
        : <span className="text-[9px] text-ash flex items-center justify-center h-full">{design.name}</span>}
      {design.source === "custom" && <span className="absolute top-1 left-1 bg-gold text-ink text-[8px] px-1 rounded uppercase tracking-wide">Design</span>}
      {imgs.length > 1 && <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[8px] px-1 rounded">{idx + 1}/{imgs.length}</span>}
      {design.weight_grams ? <span className="absolute bottom-1 left-1 bg-maroon text-pearl text-[8px] px-1 rounded">{design.weight_grams}g</span> : null}
    </button>
  );
}

const PopupField = ({ label, value }) => (
  <div>
    <div className="text-[10px] text-ash uppercase tracking-widest">{label}</div>
    <div className="text-sm">{value || "—"}</div>
  </div>
);
